import { useMemo } from 'react'
import { useInView } from 'react-intersection-observer'
import styled from 'styled-components'
import { rem } from 'polished'

import Heading from 'LuxKit/Typography/Heading'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { Experiments } from 'constants/optimizely'
import useExperiment from 'hooks/Optimizely/useExperiment'
import OfferPageHighlightTagItem from './OfferPageHighlightTagItem'
import useOfferPageHighlightTags, { type OfferPageHighlightTagContext } from './useOfferPageHighlightTags'

const HighlightGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${rem(16)};

  ${mediaQueryUp.tablet} {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
`

const ActivationSentinel = styled.div`
  height: 1px;
  opacity: 0;
  pointer-events: none;
  position: absolute;
  width: 1px;
`

interface Props {
  context?: OfferPageHighlightTagContext
  contextBuilder?: () => OfferPageHighlightTagContext
  duration?: number
  hasSelectedDates: boolean
  isOccupancyContextKnown: boolean
  propertyId: string
  rooms?: Array<App.Occupants>
}

function OfferPageHighlightTags(props: Props) {
  const [activationRef, inView] = useInView({
    triggerOnce: true,
  })
  const { isAnyVariant } = useExperiment(Experiments.croOfferPageHighlightTags, {
    canActivate: inView,
  })

  return <>
    <ActivationSentinel ref={activationRef} aria-hidden="true" data-testid="offer-page-highlight-tags-activation-sentinel" />
    {isAnyVariant && <OfferPageHighlightTagsContent {...props} />}
  </>
}

function OfferPageHighlightTagsContent(props: Props) {
  const {
    context: providedContext,
    contextBuilder,
    duration,
    hasSelectedDates,
    isOccupancyContextKnown,
    propertyId,
    rooms,
  } = props
  const context = useMemo(
    () => contextBuilder?.() ?? providedContext,
    [contextBuilder, providedContext],
  )
  const highlightTagProps = useMemo(
    () => ({
      context,
      duration,
      hasSelectedDates,
      isOccupancyContextKnown,
      propertyId,
      rooms,
    }),
    [context, duration, hasSelectedDates, isOccupancyContextKnown, propertyId, rooms],
  )
  const { title, tags } = useOfferPageHighlightTags(highlightTagProps)

  return <VerticalSpacer gap={16} data-testid="offer-page-highlight-tags">
    <Heading variant="heading3" as="h2">{title}</Heading>
    <HighlightGrid>
      {tags.map(tag => <OfferPageHighlightTagItem key={tag.id} tag={tag} />)}
    </HighlightGrid>
  </VerticalSpacer>
}

export default OfferPageHighlightTags
